import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  alpha,
  useTheme,
  Checkbox,
  ListItemText,
  IconButton,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Search as SearchIcon,
  ClearAll,
  CalendarToday,
  Warning,
  KeyboardArrowDown,
  KeyboardArrowRight
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import DownloadDetails from './ShowDownloads';
import ShowChildDownloads from './ShowChildDownloads';

// Types
interface DownloadedDocument {
  BidDocumentID: number;
  DocumentName: string;
  DownloadDate: string;
  DownloadedBy: number;
  DocumentUrl: string;
  BidID: number;
  ChildBidID: number;
}

interface SearchCriteria {
  title: string;
  categories: number[];
  statuses: number[];
  bidTypes: number[];
  adDate: Date | null;
  openingDate: Date | null;
  // showOnlyAwarded: boolean;
}

interface DownloadData {
  bidID: string;
  typeID: number;
  bidType: string;
  title: string;
  category: string;
  adDate: string;
  openingDate: string;
  status: string;
  statusID: number;
  poNumber?: string;
  downloadedDocuments: DownloadedDocument[];
  categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
  isAddendum: boolean;

  childItems?: DownloadData[];
}

// Dropdown data interfaces
interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

interface APIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: any[];
  };
}

interface DownloadChildDetailsState {
  isOpen: boolean;
  bidId: string;
  parentBidId: string;
  bidDetails: any;
}

interface BidResponse {
  BidID: number;
  TypeID: number;
  Title: string;
  CategoryIDs: number[];
  AdDate: string;
  OpeningDate: string;
  StatusID: number;
  PONumber: string | null;
  DownloadedDocuments: DownloadedDocument[];
  Categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
  Status: string;
  Type: string;
  IsAddendum: boolean;
}

const MyDownloads: React.FC = () => {
  const theme = useTheme();

  // State for view mode
  const [viewMode, setViewMode] = useState<
    'list' | 'details' | 'child-details'
  >('list');
  const [selectedBid, setSelectedBid] = useState<DownloadData | null>(null);

  const [downloadChildDetailsState, setDownloadChildDetailsState] =
    useState<DownloadChildDetailsState>({
      isOpen: false,
      parentBidId: '',
      bidId: '',
      bidDetails: {
        title: '',
        categories: '',
        type: '',
        dueDate: '',
        status: '',
        poNumber: ''
      }
    });

  // State for form fields
  const [filters, setFilters] = useState({
    title: '',
    categories: [] as number[],
    statuses: [] as number[],
    bidTypes: [] as number[],
    adDate: null as Date | null,
    openingDate: null as Date | null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);

  // State for API data
  const [downloads, setDownloads] = useState<DownloadData[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdown data
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);

  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [openStatusSelect, setOpenStatusSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [loadingChildItems, setLoadingChildItems] = useState<{
    [key: string]: boolean;
  }>({});

  // Fetch dropdown data (categories, types, statuses)
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      setDropdownError(null);
      try {
        // Fetch categories
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        } else {
          console.error('Failed to fetch categories:', categoriesResponse);
          setDropdownError('Failed to load categories. Please try again.');
        }

        // Fetch types
        const typesResponse = await httpRequest(
          'GET',
          `/bid_portal/type/get_all`,
          {},
          {}
        );

        if (typesResponse.status === 'success') {
          const typesData =
            typesResponse.data?.data || typesResponse.data?.Data || [];
          setTypes(typesData);
        } else {
          console.error('Failed to fetch types:', typesResponse);
          setDropdownError('Failed to load bid types. Please try again.');
        }

        // Fetch statuses
        const statusesResponse = await httpRequest(
          'GET',
          `/bid_portal/status/get_all`,
          {},
          {}
        );

        if (statusesResponse.status === 'success') {
          const statusesData =
            statusesResponse.data?.data || statusesResponse.data?.Data || [];
          setStatuses(statusesData);
        } else {
          console.error('Failed to fetch statuses:', statusesResponse);
          setDropdownError('Failed to load statuses. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setDropdownError('Failed to load form options. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch downloads data
  const fetchBidsWithCriteria = async (
    pageNum: number,
    pageSize: number,
    criteria: SearchCriteria = filters
  ) => {
    setIsLoading(true);
    try {
      // Construct query parameters according to API format
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(), // API uses 1-based indexing
        Size: pageSize.toString(),
        ...(criteria?.title && { Title: criteria?.title }),
        ...(criteria?.categories.length > 0 && {
          Categories: criteria?.categories.join(',')
        }),
        ...(criteria?.statuses.length > 0 && {
          Status: criteria?.statuses.join(',')
        }),
        ...(criteria?.bidTypes.length > 0 && {
          Type: criteria?.bidTypes.join(',')
        }),
        ...(criteria?.adDate && {
          AdDate: criteria?.adDate.toISOString()
        }),
        ...(criteria?.openingDate && {
          OpeningDate: criteria?.openingDate.toISOString()
        })
      });

      // API call to get downloaded bids
      const response = (await httpRequest(
        'GET',
        `/bid_portal/bids/get_downloaded?${queryParams.toString()}`
      )) as APIResponse;

      if (response.status === 'success') {
        // Transform the API response to match our component's data structure
        const transformedData: DownloadData[] = response.data.Data.map(
          (item: BidResponse) => ({
            bidID: item.BidID.toString(),
            typeID: item.TypeID,
            bidType: item.Type,
            title: item.Title,
            category: item.Categories.map((cat) => cat.CategoryName).join(', '),
            adDate: item.AdDate,
            openingDate: item.OpeningDate,
            status: item.Status,
            statusID: item.StatusID,
            poNumber: item.PONumber || undefined,
            downloadedDocuments: item.DownloadedDocuments,
            categories: item.Categories,
            isAddendum: item.IsAddendum,
            childItems: []
          })
        );

        setDownloads(transformedData);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch downloads');
      }
    } catch (error) {
      console.error('Error fetching downloads:', error);
      toast.error('Failed to fetch downloads. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDownloads = async (pageNum: number, pageSize: number) => {
    await fetchBidsWithCriteria(pageNum, pageSize);
  };

  const handleSearch = () => {
    setPage(0);
    fetchBidsWithCriteria(0, rowsPerPage);
    setExpandedRows({});
  };

  const handleClear = () => {
    // Create the cleared criteria object
    const clearedCriteria = {
      title: '',
      categories: [],
      statuses: [],
      bidTypes: [],
      adDate: null,
      openingDate: null
    };

    // Update the state
    setFilters(clearedCriteria);
    setPage(0);

    // Call fetchBids with the cleared criteria directly
    fetchBidsWithCriteria(0, rowsPerPage, clearedCriteria);
    setExpandedRows({});
  };

  // Load initial data
  useEffect(() => {
    fetchDownloads(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Handler for viewing bid details
  const handleViewDetails = (bidID: string) => {
    const bid = downloads.find((d) => d.bidID === bidID);
    if (bid) {
      setSelectedBid(bid);
      setViewMode('details');
      // Scroll to top
      window.scrollTo(0, 0);
    } else {
      toast.error('Bid details not found');
    }
  };

  // // Handler for returning to list view
  // const handleBackToList = () => {
  //   setViewMode('list');
  //   setSelectedBid(null);
  // };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to format dates nicely
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Get status color based on status text
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
      case 'active':
        return theme.palette.success.main;
      case 'closed':
      case 'close':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'awarded':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const handleDownloadsChildClick = (childBid: any, bid: DownloadData) => {
    setDownloadChildDetailsState({
      isOpen: true,
      bidId: childBid?.ChildBidID,
      parentBidId: bid.bidID,
      bidDetails: {
        title: bid.title,
        categories:
          bid?.categories?.map((cat) => cat.CategoryName).join(', ') || '',
        type: bid.bidType,
        adDate: bid.adDate,
        openingDate: bid.openingDate,
        status: bid.status,
        poNumber: bid.poNumber
      }
    });
  };

  const handleCloseDownloadChildDetails = () => {
    setDownloadChildDetailsState({
      isOpen: false,
      parentBidId: '',
      bidId: '',
      bidDetails: {
        title: '',
        categories: '',
        type: '',
        dueDate: '',
        status: '',
        poNumber: ''
      }
    });
    handleClear();
  };

  // If in details view, show the DownloadDetails component
  if (viewMode === 'details' && selectedBid) {
    // Check if the component is imported/implemented properly
    // If DownloadDetails doesn't accept onBack prop, we need to adapt
    return (
      <DownloadDetails
        bidData={selectedBid}
        setViewMode={setViewMode}
        setSelectedBid={setSelectedBid}
      />
    );

    // Note: You'll need to modify the DownloadDetails component to include
    // a back button or similar navigation, since it doesn't accept onBack prop
  }

  if (downloadChildDetailsState.isOpen) {
    return (
      <ShowChildDownloads
        bidId={downloadChildDetailsState.bidId}
        bidDetails={downloadChildDetailsState.bidDetails}
        parentBidId={downloadChildDetailsState?.parentBidId}
        onBack={handleCloseDownloadChildDetails}
      />
    );
  }

  const fetchChildItems = async (bidId: string) => {
    setLoadingChildItems((prev) => ({ ...prev, [bidId]: true }));

    try {
      const response = await httpRequest(
        'GET',
        `/bid_portal/childBid?BidID=${bidId}`,
        {},
        {}
      );

      if (response.status === 'success') {
        const childItems =
          response?.data?.data?.map((child: any) => ({
            ...child, // Keep all original properties from API
            bidId: child.ChildBidID?.toString() || child.bidId?.toString(), // Ensure bidId is set
            contractNo: child.ChildBidContractNo || child.contractNo
            // Add any other mappings you need
          })) || [];

        const updatedDownloads = downloads.map((downloadItem) => {
          return downloadItem.bidID === bidId
            ? { ...downloadItem, childItems: childItems }
            : downloadItem;
        });

        setDownloads(updatedDownloads);
      } else {
        console.error('Failed to fetch child items:', response);
        toast.error(response.message || 'Failed to load child items');
      }
    } catch (error) {
      console.error('Error fetching child items:', error);
      toast.error('Failed to fetch child items. Please try again later.');
    } finally {
      setLoadingChildItems((prev) => ({ ...prev, [bidId]: false }));
    }
  };

  const toggleRowExpansion = (bidId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [bidId]: !prev[bidId]
    }));

    if (!expandedRows[bidId]) {
      const currentDownload = downloads.find(
        (download) => download.bidID === bidId
      );
      if (
        currentDownload?.isAddendum &&
        (!currentDownload.childItems || currentDownload.childItems.length === 0)
      ) {
        fetchChildItems(bidId);
      }
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 120,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          My Downloads
        </Typography>
      </Box>

      {dropdownError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          {dropdownError}
        </Alert>
      )}

      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {loadingDropdowns ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {/* First row - 3 fields */}
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Title"
                  variant="outlined"
                  value={filters.title}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      title: e.target.value
                    })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)'
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    multiple
                    value={filters.categories}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allCategoryIDs = categories?.map(
                          (cat) => cat.CategoryID
                        );
                        const isAllSelected =
                          filters.categories?.length === categories?.length;

                        // If all categories are selected, deselect all; otherwise, select all
                        const finalCategories = isAllSelected
                          ? []
                          : allCategoryIDs;

                        setFilters({
                          ...filters,
                          categories: finalCategories
                        });
                      } else {
                        // Ensure no invalid "Unknown" category is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            categories.some((cat) => cat.CategoryID === value)
                        );

                        setFilters({
                          ...filters,
                          categories: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select categories
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((CategoryID) => {
                              const categoryName =
                                categories.find(
                                  (c) => c.CategoryID === CategoryID
                                )?.CategoryName || 'Unknown';
                              return (
                                <Chip
                                  key={CategoryID}
                                  label={categoryName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          filters.categories?.length === categories?.length
                        }
                        indeterminate={
                          filters.categories?.length > 0 &&
                          filters.categories?.length < categories?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Category Options */}
                    {categories?.map((category) => (
                      <MenuItem
                        key={category.CategoryID}
                        value={category.CategoryID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            filters.categories.indexOf(category.CategoryID) > -1
                          }
                        />
                        {category.CategoryName}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenCategorySelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Status
                  </InputLabel>
                  <Select
                    open={openStatusSelect}
                    onOpen={() => setOpenStatusSelect(true)}
                    onClose={() => setOpenStatusSelect(false)}
                    multiple
                    value={filters.statuses}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allStatusIDs = statuses?.map(
                          (status) => status.StatusID
                        );
                        const isAllSelected =
                          filters.statuses?.length === statuses?.length;

                        // If all statuses are selected, deselect all; otherwise, select all
                        const finalStatuses = isAllSelected ? [] : allStatusIDs;

                        setFilters({
                          ...filters,
                          statuses: finalStatuses
                        });
                      } else {
                        // Ensure no invalid "Unknown" status is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            statuses.some((status) => status.StatusID === value)
                        );

                        setFilters({
                          ...filters,
                          statuses: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select statuses
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((statusId) => {
                              const statusName =
                                statuses.find((s) => s.StatusID === statusId)
                                  ?.Status || 'Unknown';
                              return (
                                <Chip
                                  key={statusId}
                                  label={statusName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={filters.statuses?.length === statuses?.length}
                        indeterminate={
                          filters.statuses?.length > 0 &&
                          filters.statuses?.length < statuses?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Status Options */}
                    {statuses?.map((status) => (
                      <MenuItem
                        key={status.StatusID}
                        value={status.StatusID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            filters.statuses.indexOf(status.StatusID) > -1
                          }
                        />
                        {status.Status}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenStatusSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Type
                  </InputLabel>
                  <Select
                    open={openTypeSelect}
                    onOpen={() => setOpenTypeSelect(true)}
                    onClose={() => setOpenTypeSelect(false)}
                    multiple
                    value={filters.bidTypes}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allTypeIDs = types?.map((type) => type.TypeID);
                        const isAllSelected =
                          filters.bidTypes?.length === types?.length;

                        // If all types are selected, deselect all; otherwise, select all
                        const finalTypes = isAllSelected ? [] : allTypeIDs;

                        setFilters({
                          ...filters,
                          bidTypes: finalTypes
                        });
                      } else {
                        // Ensure no invalid "Unknown" type is included
                        const validSelectedValues = selectedValues.filter(
                          (value) => types.some((type) => type.TypeID === value)
                        );

                        setFilters({
                          ...filters,
                          bidTypes: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select types
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((TypeID) => {
                              const TypeName =
                                types.find((t) => t.TypeID === TypeID)?.Type ||
                                'Unknown';
                              return (
                                <Chip
                                  key={TypeID}
                                  label={TypeName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={filters.bidTypes?.length === types?.length}
                        indeterminate={
                          filters.bidTypes?.length > 0 &&
                          filters.bidTypes?.length < types?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Type Options */}
                    {types?.map((type) => (
                      <MenuItem
                        key={type.TypeID}
                        value={type.TypeID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={filters.bidTypes.indexOf(type.TypeID) > -1}
                        />
                        {type.Type}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenTypeSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Picker */}
              <Grid item xs={12} sm={4} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Ad Date"
                    value={filters.adDate}
                    onChange={(date) =>
                      setFilters({ ...filters, adDate: date })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <CalendarToday
                              sx={{
                                color: theme.palette.text.secondary,
                                mr: 1,
                                fontSize: '1rem'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: 1.5,
                            height: 40,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.5
                              )
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2
                            }
                          }
                        },
                        sx: {
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            transform: 'translate(14px, 11px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 500
                          }
                        }
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Opening Date"
                    value={filters.openingDate}
                    onChange={(date) =>
                      setFilters({ ...filters, openingDate: date })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <CalendarToday
                              sx={{
                                color: theme.palette.text.secondary,
                                mr: 1,
                                fontSize: '1rem'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: 1.5,
                            height: 40,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.5
                              )
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2
                            }
                          }
                        },
                        sx: {
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            transform: 'translate(14px, 11px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 500
                          }
                        }
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    height: '100%',
                    alignItems: 'center'
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    startIcon={<ClearAll />}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      borderWidth: '1px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderWidth: '1px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            My Downloads
          </Typography>
          {!isLoading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Total Results: {totalCount}
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Ad Date</TableCell>
                    <TableCell>Opening Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Documents</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No downloads found. Try adjusting your search
                            criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    downloads.map((download) => (
                      <React.Fragment key={download.bidID}>
                        <TableRow
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.03)
                            }
                          }}
                        >
                          <TableCell>
                            {download.isAddendum && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  toggleRowExpansion(download.bidID)
                                }
                                sx={{
                                  transition: 'transform 0.2s ease',
                                  color: theme.palette.primary.main
                                }}
                              >
                                {expandedRows[download.bidID] ? (
                                  <KeyboardArrowDown />
                                ) : (
                                  <KeyboardArrowRight />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={download.bidType}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 500,
                                borderRadius: 1
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {download.title}
                          </TableCell>
                          <TableCell>{download.category}</TableCell>
                          <TableCell>{download.adDate ? new Date(download.adDate).toLocaleDateString('en-US') : '-'}</TableCell>
                          <TableCell>{formatDate(download.openingDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={download.status}
                              size="small"
                              sx={{
                                bgcolor: alpha(
                                  getStatusColor(download.status),
                                  0.1
                                ),
                                color: getStatusColor(download.status),
                                fontWeight: 'bold',
                                borderRadius: 1
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            {download.downloadedDocuments?.length || 0}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              color="primary"
                              variant="contained"
                              onClick={() => handleViewDetails(download.bidID)}
                              sx={{
                                borderRadius: 1.5,
                                px: 2,
                                py: 0.5,
                                fontWeight: 'bold'
                              }}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Nested child items table */}
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse
                              in={expandedRows[download.bidID]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 2 }}>
                                {loadingChildItems[download.bidID] ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      p: 3
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ ml: 2 }}
                                    >
                                      Loading child items...
                                    </Typography>
                                  </Box>
                                ) : download.childItems &&
                                  download.childItems.length > 0 ? (
                                  <Table
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.grey[100],
                                        0.5
                                      )
                                    }}
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Type
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Title
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Category
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Bid Deadline
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Status
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {download.childItems.map(
                                        (childBid: any) => (
                                          <TableRow key={childBid.ChildBidID}>
                                            <TableCell
                                              sx={{ fontWeight: 500 }}
                                            ></TableCell>
                                            <TableCell
                                              sx={{ fontWeight: 500 }}
                                            ></TableCell>
                                            <TableCell
                                              sx={{ fontWeight: 500 }}
                                            ></TableCell>
                                            <TableCell
                                              sx={{ fontWeight: 500 }}
                                            ></TableCell>
                                            <TableCell
                                              sx={{ fontWeight: 500 }}
                                            ></TableCell>
                                            <TableCell>
                                              <Button
                                                size="small"
                                                color="primary"
                                                variant="contained"
                                                onClick={() =>
                                                  handleDownloadsChildClick(
                                                    childBid,
                                                    download
                                                  )
                                                }
                                                sx={{
                                                  borderRadius: 1.5,
                                                  px: 2,
                                                  py: 0.5,
                                                  fontWeight: 'bold'
                                                }}
                                              >
                                                Details
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ p: 2, textAlign: 'center' }}
                                  >
                                    No child items found for this bid.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MyDownloads;
